// src/pages/CRM/Core/TagsList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  LocalOffer as TagIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';

const TagsList = () => {
  const navigate = useNavigate();
  const { chatbotId } = useParams();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    color: '#1976d2',
    description: '',
    applies_to_contacts: true,
    applies_to_accounts: true,
    applies_to_leads: true
  });

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/crm/tags');
      if (response.data.success) {
        setTags(response.data.data || []);
      }
    } catch (err) {
      setError('Failed to load tags');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleDelete = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) return;
    
    try {
      const response = await axiosInstance.delete(`/api/crm/tags/${tagId}`);
      if (response.data.success) {
        fetchTags();
      }
    } catch (err) {
      alert('Failed to delete tag');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    
    setSubmitting(true);
    try {
      const response = await axiosInstance.post('/api/crm/tags', formData);
      if (response.data.success) {
        setOpenDialog(false);
        setFormData({
          name: '',
          color: '#1976d2',
          description: '',
          applies_to_contacts: true,
          applies_to_accounts: true,
          applies_to_leads: true
        });
        fetchTags();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create tag');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Typography color="text.primary">Tags</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Manage Tags
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Create Tag
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tag Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Applies To</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                </TableRow>
              ) : tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>No tags defined.</TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow key={tag.id} hover>
                    <TableCell>
                      <Chip 
                        label={tag.name} 
                        size="small" 
                        sx={{ bgcolor: tag.color, color: '#fff', fontWeight: 'bold' }} 
                        icon={<TagIcon style={{ color: '#fff' }} />}
                      />
                    </TableCell>
                    <TableCell>{tag.description || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {tag.applies_to_contacts && <Chip label="Contacts" size="small" variant="outlined" />}
                        {tag.applies_to_accounts && <Chip label="Accounts" size="small" variant="outlined" />}
                        {tag.applies_to_leads && <Chip label="Leads" size="small" variant="outlined" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => handleDelete(tag.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tag</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Tag Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Applies To:</Typography>
              <FormControlLabel
                control={<Checkbox checked={formData.applies_to_contacts} onChange={(e) => setFormData({...formData, applies_to_contacts: e.target.checked})} />}
                label="Contacts"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.applies_to_accounts} onChange={(e) => setFormData({...formData, applies_to_accounts: e.target.checked})} />}
                label="Accounts"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.applies_to_leads} onChange={(e) => setFormData({...formData, applies_to_leads: e.target.checked})} />}
                label="Leads"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!formData.name || submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Create Tag'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagsList;